// \src\pages\produto\page.tsx

import { useState } from "react";
import { useParams, Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import SellerSidebar from "@/components/feature/SellerSidebar";
import { useAdmin } from "@/context/AdminContext";

function getLocalDateString(): string {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
}

function isProductVisible(product: {
  active: boolean;
  activeUntil: string;
}): boolean {
  if (!product.active) {
    return false;
  }

  if (!product.activeUntil) {
    return true;
  }

  return product.activeUntil >= getLocalDateString();
}

export default function ProdutoDetalhe() {
  const { slug } = useParams<{ slug: string }>();
  const { data } = useAdmin();
  const products = data.products.filter(isProductVisible);
  const categories = data.categories;
  const s = data.settings;
  const phoneDigits = s.contactPhoneCentral.replace(/\D/g, "");
  const phoneHref = phoneDigits.startsWith("55") ? `tel:+${phoneDigits}` : `tel:+55${phoneDigits}`;
  const product = products.find((p) => p.slug === slug);
  const [activeImg, setActiveImg] = useState(0);

  if (!product) {
    return (
      <>
        <Navbar />
        <main className="pt-20 min-h-screen bg-background-50 flex items-center justify-center">
          <div className="text-center">
            <i className="ri-box-3-line text-6xl text-foreground-300 mb-4 block" />
            <h2 className="text-2xl font-heading font-bold text-foreground-700 mb-2">Produto não encontrado</h2>
            <Link to="/produtos" className="text-primary-500 hover:text-primary-600 font-label text-sm cursor-pointer">Voltar para Produtos</Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const category = categories.find((c) => c.id === product.category);
  const subcategory = category?.subcategories.find((s) => s.id === product.subcategoryId);
  const relatedProducts = products.filter((p) => p.category === product.category && p.id !== product.id).slice(0, 4);

  return (
    <>
      <Navbar />
      <main className="pt-20 min-h-screen bg-background-50">
        <section className="bg-background-100 border-b border-background-200 px-4 md:px-8 py-3">
          <div className="max-w-7xl mx-auto">
            <nav className="text-xs text-foreground-500 font-label flex flex-wrap gap-1 items-center">
              <Link to="/" className="hover:text-primary-500 cursor-pointer">Início</Link>
              <span>/</span>
              <Link to="/produtos" className="hover:text-primary-500 cursor-pointer">Produtos</Link>
              {category && <><span>/</span><Link to={`/produtos?categoria=${category.id}`} className="hover:text-primary-500 cursor-pointer">{category.name}</Link></>}
              {subcategory && <><span>/</span><span className="text-primary-500">{subcategory.name}</span></>}
            </nav>
          </div>
        </section>

        <section className="max-w-7xl mx-auto px-4 md:px-8 py-8">
          <div className="flex flex-col lg:flex-row gap-8">
            <div className="flex-1 min-w-0">
              <div className="flex flex-col md:flex-row gap-6 mb-8">
                <div className="w-full md:w-96 shrink-0">
                  <div className="bg-background-100 rounded-lg overflow-hidden h-80 mb-3">
                    <img src={product.images[activeImg]} alt={product.name} className="w-full h-full object-cover object-top" />
                  </div>
                  {product.images.length > 1 && (
                    <div className="flex gap-2">
                      {product.images.map((img, i) => (
                        <button key={i} onClick={() => setActiveImg(i)} className={`w-16 h-16 rounded-md overflow-hidden border-2 transition-all cursor-pointer ${i === activeImg ? "border-primary-500" : "border-background-200"}`}>
                          <img src={img} alt="" className="w-full h-full object-cover" />
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <div className="flex-1">
                  {product.promoLabel && <span className="inline-block bg-primary-500 text-background-50 text-xs font-label font-bold px-3 py-1 rounded-full mb-3">{product.promoLabel}</span>}
                  <p className="text-foreground-400 text-sm font-label mb-1">{product.brand}</p>
                  <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-2 leading-tight">{product.name}</h1>
                  {product.code && <div className="mb-3"><span className="inline-flex items-center bg-primary-50 border border-primary-100 text-primary-700 text-xs font-label font-semibold px-3 py-1.5 rounded-md">Código do produto: {product.code}</span></div>}
                  {product.showPrice ? (
                    <div className="mb-4">
                      <p className="text-3xl font-heading font-bold text-primary-600">R$ {product.price.toFixed(2).replace(".", ",")}</p>
                      <p className="text-xs text-foreground-400 font-label mt-1">Preço sujeito a alteração. Consulte disponibilidade.</p>
                    </div>
                  ) : (
                    <div className="mb-4 bg-primary-50 border border-primary-100 rounded-md p-3">
                      <p className="text-primary-700 font-label text-sm font-medium"><i className="ri-information-line mr-1" />Entre em contato para consultar o preço deste produto</p>
                    </div>
                  )}
                  <p className="text-foreground-600 text-sm leading-relaxed mb-5">{product.description}</p>
                  {product.specs.length > 0 && (
                    <div className="mb-5">
                      <h3 className="font-label font-semibold text-foreground-900 text-sm mb-2">Especificações</h3>
                      <ul className="space-y-1.5">
                        {product.specs.map((spec, i) => (
                          <li key={i} className="flex items-center gap-2 text-sm text-foreground-600 font-label"><i className="ri-checkbox-circle-line text-primary-500 shrink-0" /><span dangerouslySetInnerHTML={{ __html: spec }} /></li>
                        ))}
                      </ul>
                    </div>
                  )}
                  <div className="flex flex-wrap gap-3">
                    <a href={s.socialWhatsapp} target="_blank" rel="nofollow noreferrer" className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-background-50 px-6 py-3 rounded-md font-label font-semibold text-sm transition-all cursor-pointer whitespace-nowrap"><i className="ri-whatsapp-line text-base" /> Pedir pelo WhatsApp</a>
                    <a href={phoneHref} className="flex items-center gap-2 bg-foreground-900 hover:bg-primary-500 text-background-50 px-6 py-3 rounded-md font-label font-semibold text-sm transition-all cursor-pointer whitespace-nowrap"><i className="ri-phone-line text-base" /> Ligar agora</a>
                  </div>
                </div>
              </div>

              {relatedProducts.length > 0 && (
                <div>
                  <h2 className="text-xl font-heading font-bold text-foreground-950 mb-4">Produtos Relacionados</h2>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                    {relatedProducts.map((rp) => (
                      <Link key={rp.id} to={`/produto/${rp.slug}`} className="group bg-background-50 border border-background-200 rounded-lg overflow-hidden hover:border-primary-300 transition-all cursor-pointer">
                        <div className="h-28 overflow-hidden"><img src={rp.images[0]} alt={rp.name} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500" /></div>
                        <div className="p-2">
                          <p className="text-foreground-900 font-label font-semibold text-xs line-clamp-2">{rp.name}</p>
                          {rp.code && <span className="inline-flex items-center bg-primary-50 border border-primary-100 text-primary-700 text-[10px] font-label font-semibold px-2 py-0.5 rounded mt-1">Código: {rp.code}</span>}
                          {rp.showPrice && <p className="text-primary-600 font-heading font-bold text-sm mt-1">R$ {rp.price.toFixed(2).replace(".", ",")}</p>}
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="w-full lg:w-64 shrink-0">
              <div className="sticky top-28"><SellerSidebar /></div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}